import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../app/session";
import { ApiError, api } from "../lib/api";
import { syncEngine } from "../lib/sync";
import { unlockWithKeyMaterial } from "../lib/encryption";
import { AuthShell } from "../components/AuthShell";
import { Button } from "../components/ui";
import { useT } from "../i18n/index.js";
import { cn } from "../lib/cn";

/**
 * Приём ключа со второго устройства: сюда ведёт квадрат из настроек.
 *
 * Адрес выглядит так:
 *
 *     https://…/join?space=<id>&invite=<код>#k=<ключ>
 *
 * Ключ стоит **после решётки**, и это единственное, что делает всю передачу
 * безопасной: фрагмент браузеры на сервер не отправляют — ни в запросе, ни в
 * Referer, — поэтому ключ существует ровно между двумя телефонами в одной
 * комнате. Приглашение, наоборот, в query: его проверяет сервер, и секретом оно
 * не является.
 *
 * Фрагмент стирается из адреса **первым же действием**, ещё до того, как экран
 * что-нибудь спросит у сервера: иначе ключ остался бы в адресной строке, в
 * истории, в списке вкладок и на снимке экрана, который человек сделает, чтобы
 * прислать нам «вот, не работает». Отсюда и то, что материал живёт в памяти
 * модуля: ни `localStorage`, ни `sessionStorage` для ключа не годятся — из
 * первого он переживёт закрытие вкладки, а нужен он на секунду.
 */

/** Пойманный ключ. Только память вкладки; переживает переход на вход и обратно. */
let handed: { spaceId: string; material: string } | null = null;

/**
 * Забрать ключ из адреса и немедленно вычистить адрес.
 *
 * Вызывается в теле первого рендера, до любого `await`. Повторный вызов —
 * второй проход StrictMode, возвращение со страницы входа — фрагмента уже не
 * находит и отвечает тем, что поймал в первый раз.
 */
function captureKey(spaceId: string | null): string | null {
  const fragment = window.location.hash;

  if (fragment.length > 1) {
    const material = new URLSearchParams(fragment.slice(1)).get("k");
    history.replaceState(null, "", window.location.pathname + window.location.search);
    if (material && spaceId) handed = { spaceId, material };
  }

  return handed && handed.spaceId === spaceId ? handed.material : null;
}

/** Чем кончилось. «working» — ещё ничем. */
type Phase =
  | "working"
  | "accepting"
  | "signin"
  | "noKey"
  | "brokenKey"
  | "notMember"
  | "failed";

export function JoinPage() {
  const t = useT();
  const navigate = useNavigate();
  const { session, loading, refresh, selectSpace } = useSession();
  const [params] = useSearchParams();

  const spaceId = params.get("space");
  const inviteCode = params.get("invite");

  // Первое, что делает экран, — и в этом весь смысл: ключ уходит из адреса
  // раньше, чем страница успевает куда-нибудь сходить.
  const [material] = useState(() => captureKey(spaceId));

  const [phase, setPhase] = useState<Phase>("working");
  const [failure, setFailure] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // Сессия живёт в куке, которую подтверждает сервер: пока первый ответ не
    // пришёл, «не вошёл» — это не ответ, а незнание.
    if (loading) return;

    if (!session) {
      setPhase("signin");
      return;
    }

    if (!spaceId || !material) {
      setPhase("noKey");
      return;
    }

    // Ровно один проход: `refresh` ниже меняет сессию, а с ней и зависимости
    // этого эффекта — второй заход посреди первого принял бы приглашение дважды.
    if (started.current) return;
    started.current = true;

    let alive = true;

    void (async () => {
      try {
        let member = session.spaces.some((space) => space.id === spaceId);

        if (!member && inviteCode) {
          setPhase("accepting");
          await api.acceptInvite(inviteCode);
          member = true;
        }

        if (!member) {
          if (alive) setPhase("notMember");
          return;
        }

        // Ключ ложится на устройство только после того, как выяснилось, что
        // человека в это пространство пускают: ключ от комнаты, куда не
        // пустили, — мусор в хранилище.
        try {
          await unlockWithKeyMaterial(spaceId, material);
        } catch {
          if (alive) setPhase("brokenKey");
          return;
        }

        // Больше он не нужен нигде: пришёл, лёг на устройство, забыт.
        handed = null;

        // Порядок не случаен: выбор пространства пишет предпочтение, а `refresh`
        // читает его, собирая сессию заново — уже с новым пространством внутри.
        selectSpace(spaceId);
        await refresh();
        await syncEngine.pull(spaceId);

        // `replace`, чтобы «назад» не вернуло на экран без ключа в адресе.
        navigate("/lists", { replace: true });
      } catch (cause) {
        if (!alive) return;
        setFailure(cause instanceof ApiError ? cause.message : null);
        setPhase("failed");
      }
    })();

    return () => {
      alive = false;
    };
  }, [loading, session, spaceId, material, inviteCode, navigate, refresh, selectSpace]);

  const back = (
    <Link to="/lists" className="text-accent underline underline-offset-4">
      {t("encryption.joinBack")}
    </Link>
  );

  if (phase === "signin") {
    /*
     * Ключ остаётся в памяти вкладки, поэтому обе дороги возвращают сюда:
     * экран войдёт с тем же `?space=`, найдёт материал там, где его оставил, и
     * доведёт дело до конца. Сканировать заново не придётся — пока вкладку не
     * закрыли.
     */
    const comeBack = { from: { pathname: "/join", search: window.location.search } };

    /*
     * Регистрация впереди входа, и код приглашения едет с ней.
     *
     * Квадрат показывают тому, кого зовут, — то есть почти всегда человеку без
     * учётной записи. Отправлять его на вход значит показать форму, которую ему
     * нечем заполнить; а не передать код значит заставить набирать руками то,
     * что он только что отсканировал. Оба раза человек делает работу, которую
     * квадрат уже сделал за него.
     */
    const register = inviteCode
      ? `/register?code=${encodeURIComponent(inviteCode)}`
      : "/register";

    return (
      <AuthShell
        title={t("encryption.joinSignInTitle")}
        lede={t(inviteCode ? "encryption.joinRegisterBody" : "encryption.joinSignInBody")}
        footer={back}
      >
        <div className="flex flex-col gap-2">
          {inviteCode ? (
            <Link
              to={register}
              state={comeBack}
              className="inline-flex min-h-[var(--touch)] w-full items-center justify-center rounded-pill bg-accent px-5 text-[0.9375rem] font-semibold text-ground"
            >
              {t("encryption.joinRegisterAction")}
            </Link>
          ) : null}

          <Link
            to="/signin"
            state={comeBack}
            className={cn(
              "inline-flex min-h-[var(--touch)] w-full items-center justify-center rounded-pill px-5",
              "text-[0.9375rem] font-semibold transition-colors",
              inviteCode
                ? "bg-surface-alt text-ink hover:bg-line"
                : "bg-accent text-ground",
            )}
          >
            {t("encryption.joinSignInAction")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (phase === "noKey" || phase === "brokenKey" || phase === "notMember") {
    const wording = {
      noKey: ["encryption.joinBadLinkTitle", "encryption.joinBadLinkBody"],
      brokenKey: ["encryption.joinBrokenKeyTitle", "encryption.joinBrokenKeyBody"],
      notMember: ["encryption.joinNotMemberTitle", "encryption.joinNotMemberBody"],
    } as const;

    const [title, body] = wording[phase];

    return (
      /* Без `footer`: он говорит ровно то, что говорит кнопка ниже, и «К
         спискам» дважды подряд читается как две разные дороги. */
      <AuthShell title={t(title)} lede={t(body)}>
        <Button full onClick={() => navigate("/lists", { replace: true })}>
          {t("encryption.joinBack")}
        </Button>
      </AuthShell>
    );
  }

  if (phase === "failed") {
    return (
      <AuthShell
        title={t("encryption.joinFailedTitle")}
        lede={failure ?? t("encryption.joinRetryHint")}
      >
        <Button full onClick={() => navigate("/lists", { replace: true })}>
          {t("encryption.joinBack")}
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("encryption.joinTitle")}
      lede={
        phase === "accepting" ? t("encryption.joinAccepting") : t("encryption.joinWorking")
      }
      footer={back}
    >
      <div className="grid place-items-center py-6 text-muted">
        <span className="sr-only">{t("common.loading")}</span>
        <span aria-hidden="true" className="size-6 animate-pulse rounded-full bg-line" />
      </div>
    </AuthShell>
  );
}
